import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { useNavigate } from 'react-router-dom';
import { queryKeys } from '@/lib/query-client.js';

export type Notification = components['schemas']['Notification'];
export type NotificationType = Notification['type'];
export type NotificationPreferences = components['schemas']['NotificationPreferences'];

/** How often the badge re-checks. */
const BADGE_POLL_MS = 60_000;

export interface NotificationList {
  items: Notification[];
  unreadCount: number;
}

/**
 * Shared so the bell can warm the list without a second copy of the request.
 *
 * Two definitions of the same query is how a prefetch ends up populating a
 * key nothing reads, which looks exactly like the prefetch not working.
 */
export const notificationsQuery = {
  queryKey: queryKeys.notifications.list({}),
  queryFn: () => api.get<NotificationList>('/notifications', { query: { limit: 30 } }),
};

export function useNotifications(enabled = true) {
  return useQuery({ ...notificationsQuery, enabled });
}

/**
 * Just the badge number.
 *
 * A counting query on its own endpoint rather than reading the list: fetching
 * thirty rows every minute to render one number is a waste of both ends, and
 * the dropdown is closed almost all of the time.
 *
 * The bell warms the list when this number *changes*, which is not the same
 * thing — that is once per notification rather than once per minute, and it
 * is what makes opening the bell show the new one rather than the old set
 * with a pause after it.
 */
/**
 * The number on the bell.
 *
 * **It polls, and it also re-checks the moment the tab comes back**
 * (orbit-ui#75). Those are not the same thing and the first alone was not
 * enough: a polling interval does not run while the tab is hidden, so
 * somebody who applied for leave, switched tabs, had it approved and came
 * back saw the old number until the next tick happened to land — up to a
 * minute of the bell quietly lying, and "it only updates if I refresh".
 *
 * Coming back to the tab is exactly when somebody looks at the bell, so it is
 * exactly when the number has to be right. This is a deliberate exception to
 * the client's `refetchOnWindowFocus: false` default — most of an HRMS moves
 * slowly enough not to need it; a notification count is the thing that does.
 *
 * `staleTime: 0` beside it is load-bearing. A focus refetch only happens for
 * a query that is **stale**, and the default keeps everything fresh for a
 * minute — which is the same minute the poll would have covered anyway, so
 * without this the fix would do nothing for exactly the gap it is meant to
 * close. A count is one integer; re-asking for it costs nothing worth saving.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: queryKeys.notifications.unread(),
    queryFn: () => api.get<{ unreadCount: number }>('/notifications/unread-count'),
    refetchInterval: BADGE_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids?: string[]) =>
      api.post<{ marked: number }>('/notifications/read', ids ? { ids } : {}),

    /**
     * Applied before the request leaves, and rolled back if it fails.
     *
     * Marking something read is the one thing here that cannot be wrong for
     * long: the answer is already known, and waiting a round trip to remove a
     * tint somebody just dismissed reads as the button not having worked.
     */
    onMutate: async (ids) => {
      // Otherwise a refetch already in flight can land after this and put the
      // old values back.
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all() });

      const previousList = queryClient.getQueryData<NotificationList>(
        queryKeys.notifications.list({}),
      );
      const previousCount = queryClient.getQueryData<{ unreadCount: number }>(
        queryKeys.notifications.unread(),
      );

      const marking = ids ? new Set(ids) : null;
      const readAt = new Date().toISOString();

      /**
       * How many unread rows this actually clears.
       *
       * Counted from the list where it is loaded, because the ids given may
       * include something already read. Where it is not, the caller only ever
       * marks rows it is showing as unread, so the count of ids is right.
       */
      const clearing = marking
        ? (previousList?.items.filter((item) => !item.readAt && marking.has(item.id)).length ??
          marking.size)
        : Infinity;

      queryClient.setQueryData<NotificationList>(queryKeys.notifications.list({}), (old) =>
        old
          ? {
              items: old.items.map((item) =>
                item.readAt || (marking && !marking.has(item.id)) ? item : { ...item, readAt },
              ),
              unreadCount: Math.max(0, old.unreadCount - clearing),
            }
          : old,
      );

      // Not derived from the list: the list is the most recent thirty, and the
      // badge counts everything. Marking one of thirty must not reset a badge
      // that is counting fifty.
      queryClient.setQueryData<{ unreadCount: number }>(queryKeys.notifications.unread(), (old) =>
        old ? { unreadCount: Math.max(0, old.unreadCount - clearing) } : old,
      );

      return { previousList, previousCount };
    },

    onError: (_error, _ids, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(queryKeys.notifications.list({}), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(queryKeys.notifications.unread(), context.previousCount);
      }
    },

    // Whatever happened, the server has the last word.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => api.get<NotificationPreferences>('/notifications/preferences'),
  });
}

export function useSavePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: { email?: Record<string, boolean>; showBirthday?: boolean }) =>
      api.put<NotificationPreferences>('/notifications/preferences', changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    },
  });
}

/**
 * Opening a notification: mark it read, refresh, and follow its link.
 *
 * **The refresh is the point** (orbit-ui#48). A notification says something
 * changed, and `navigate` to the route somebody is already on is a no-op — so
 * clicking “your leave was approved” while sitting on the leave page moved
 * nothing and changed nothing on screen, which reads as a dead link.
 *
 * Everything is invalidated rather than only the feature the link names. The
 * alternative is a map from routes to query keys, which is a second copy of
 * what the router already knows and would go stale the first time somebody
 * adds a notification type. React Query refetches what is mounted and marks
 * the rest stale, so the cost is one round trip on a click a person made.
 *
 * Shared by the bell and the notifications page, which had the same three
 * lines each and would have been fixed one at a time otherwise.
 */
export function useOpenNotification(): (item: Notification) => void {
  const markRead = useMarkRead();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (item) => {
    if (!item.readAt) markRead.mutate([item.id]);
    if (item.link) void navigate(item.link);
    void queryClient.invalidateQueries();
  };
}

/**
 * What each notification type is called in the preferences screen, and what
 * it actually means.
 *
 * Only the ones somebody can switch off appear. The server drops the rest, so
 * a type missing here simply never renders — which is the right failure for a
 * screen of checkboxes.
 *
 * **Every row carries a line of its own (orbit-ui#173).** Three of them read
 * "waiting on you", which says nothing about *whose* leave, or why somebody
 * who approves nothing is being offered the switch at all. A label is a name;
 * these say when the email arrives and who it is about.
 */
export interface NotificationPreference {
  label: string;
  /** When this one is sent, in the words of somebody receiving it. */
  hint: string;
}

export const NOTIFICATION_LABELS: Record<string, NotificationPreference> = {
  LEAVE_SUBMITTED: {
    label: 'Leave waiting on you',
    hint: 'Somebody whose leave you approve has applied, and it is waiting for your decision. Only ever sent to approvers.',
  },
  LEAVE_APPROVED: {
    label: 'Your leave decided',
    hint: 'Your own leave request has been approved or rejected.',
  },
  LEAVE_REJECTED: {
    label: 'Your leave decided',
    hint: 'Your own leave request has been approved or rejected.',
  },
  REGULARIZATION_SUBMITTED: {
    label: 'Attendance corrections waiting on you',
    hint: 'Somebody in your team has asked you to correct a day of their attendance. Only ever sent to approvers.',
  },
  REGULARIZATION_ACTIONED: {
    label: 'Your attendance correction decided',
    hint: 'A correction you asked for has been approved or rejected.',
  },
  LATE_MARK_WARNING: {
    label: 'Late mark alerts',
    // Sent to the manager and HR, never to the person who was late
    // (orbit-ui#44). A hint saying "you have been late" would be wrong for
    // everybody who receives it.
    hint: 'Somebody you manage has gone past the month’s late-mark allowance. It goes to their manager and HR, not to them.',
  },
  MISSING_PUNCH_REMINDER: {
    label: 'Missing punch reminders',
    hint: 'One of your own days is missing a check-in or check-out, and the days you have left to correct it are running out.',
  },
  ANNOUNCEMENT_PUBLISHED: {
    label: 'Announcements',
    // Sent as it is published, not in the next morning's digest
    // (orbit-ui#177).
    hint: 'A company announcement has been published. It arrives as it is published, not the next morning.',
  },
  PROFILE_CHANGE_REQUESTED: {
    label: 'Profile changes waiting on you',
    hint: 'Somebody has asked HR to correct their record, and you are one of the people who may decide it. Only ever sent to approvers.',
  },
  PROFILE_CHANGE_ACTIONED: {
    label: 'Your profile change decided',
    hint: 'A correction you asked HR for has been approved or rejected.',
  },
};
